import { Link } from "react-router-dom";
import { Check, Phone, Mail, ArrowRight, ShieldCheck, Heart, Users, MapPin, Home } from "lucide-react";
import { CONTACT_INFO, IMAGES } from "../data";
import ContactForm from "../components/ContactForm";
import Meta from "../components/Meta";
import { motion } from "framer-motion";
import { fadeIn, fadeInSlow, slideInLeft, slideInRight, staggerContainer, staggerItem } from "../lib/animations";

export default function OmOs() {
  const values = [
    {
      title: "Solidt håndværk",
      desc: "Vi går aldrig på kompromis med kvaliteten. Vi bruger de bedste materialer og udfører arbejdet med millimeterpræcision, så det holder i mange år fremover.",
      icon: <ShieldCheck className="w-6 h-6 text-amber-500" />
    },
    {
      title: "Personlig rådgivning",
      desc: "Hvert projekt er unikt. Vi lytter til dine ønsker, rådgiver om de bedste løsninger og holder en tæt dialog gennem hele byggeprocessen.",
      icon: <Heart className="w-6 h-6 text-amber-500" />
    },
    {
      title: "Lokal forankring",
      desc: "Med base i Helsingør kender vi lokalområdet og de nordsjællandske byggetraditioner. Vi er din lokale tømrer, du altid kan regne med.",
      icon: <MapPin className="w-6 h-6 text-amber-500" />
    }
  ];

  return (
    <>
      <Meta
        title="Om os"
        description="Læs om Nordsjællands Tømrer og Snedker i Helsingør. Vi bygger på solidt håndværk, personlig rådgivning og stærk lokal forankring."
      />

      {/* Page Header Banner */}
      <section className="relative pt-40 pb-24 bg-zinc-950 text-white rounded-none overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.omos}
            alt="Om Nordsjællands Tømrer og Snedker"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-zinc-950" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12"
        >
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-amber-500 block mb-3">
            Hvem er vi?
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light uppercase tracking-wide text-stone-50">
            Om <span className="font-semibold text-amber-500">Nordsjællands Tømrer og Snedker</span>
          </h1>
          <p className="mt-4 text-stone-300 max-w-2xl font-light leading-relaxed text-sm sm:text-base">
            Lær os bedre at kende. Vi er et dedikeret tømrer- og snedkerfirma med base i Helsingør, der bygger på tillid og høj faglig stolthed.
          </p>
        </motion.div>
      </section>

      {/* Main Content Section */}
      <section className="bg-stone-50 py-24 px-6 md:px-12 text-zinc-900 rounded-none overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          {/* Left Column: Detailed Copy */}
          <motion.div
            variants={slideInLeft}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-7 space-y-6"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-amber-700">
              Vores historie & vision
            </span>
            <h2 className="text-2xl md:text-3xl font-semibold uppercase tracking-wide text-zinc-900">
              Din trygge samarbejdspartner i Nordsjælland
            </h2>
            <p className="text-stone-600 leading-relaxed font-light">
              Nordsjællands Tømrer og Snedker blev stiftet med en klar vision: at levere tømrerarbejde af højeste kvalitet med fokus på godt håndværk, ærlig rådgivning og holdbare løsninger. Med mere end 20 års erfaring i branchen sikrer vi, at dit projekt udføres professionelt og præcist. Vi oplever ofte, at byggeprojekter kan virke uoverskuelige for boligejere. Derfor har vi gjort det til vores mission at gøre processen så enkel, gennemskuelig og tryg som muligt.
            </p>
            <p className="text-stone-600 leading-relaxed font-light">
              Vi holder til på Esrumvej i Helsingør, og vores primære arbejdsområde strækker sig over hele Nordsjælland. Vi udfører opgaver for både private, erhverv og boligforeninger. Uanset om det drejer sig om en mindre reparation af et vindue eller opførelsen af en stor, arkitekttegnet træterrasse, går vi til opgaven med samme engagement og faglige stolthed.
            </p>
            <p className="text-stone-600 leading-relaxed font-light">
              For os er en aftale en aftale. Vi overholder tidsplaner, rydder pænt op efter os selv og afleverer altid et resultat, som vi selv kan være stolte af – og som du kan glæde dig over i mange år fremover.
            </p>
          </motion.div>

          {/* Right Column: Visual & Quick Info */}
          <motion.div
            variants={slideInRight}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="lg:col-span-5 space-y-8"
          >
            <div className="relative h-[350px] overflow-hidden rounded-none border border-stone-200">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/bf156273-6242-48dd-a9a0-7699f46947d9/ambassada-r-for-3-byggetilbud-2026.jpeg"
                alt="Ambassadør for 3byggetilbud 2026"
                className="w-full h-full object-cover"
              />
            </div>
            
            <div className="bg-zinc-900 text-white p-6 border border-zinc-800 rounded-none flex items-center gap-4">
              <Users className="w-10 h-10 text-amber-500 shrink-0" />
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-stone-200">Erfarne fagfolk</h4>
                <p className="text-[11px] text-stone-400 leading-relaxed font-light mt-1">
                  Vores team består af engagerede og veluddannede tømrere med mere end 20 års erfaring i branchen.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-zinc-900 py-24 px-6 md:px-12 text-white rounded-none border-t border-zinc-800 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeIn}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="text-center max-w-3xl mx-auto mb-16 space-y-4"
          >
            <span className="text-xs font-bold uppercase tracking-widest text-amber-500">
              Vores fundament
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold uppercase tracking-wider text-stone-50">
              De tre grundpiller i Nordsjællands Tømrer og Snedker
            </h2>
            <p className="text-stone-400 font-light leading-relaxed">
              Vores arbejde og daglige gang bygger på tre enkle værdier, som sikrer dig det bedste forløb og resultat.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-12 gap-8"
          >
            {values.map((val, idx) => (
              <motion.div
                key={idx}
                variants={staggerItem}
                className={`bg-zinc-950 border border-zinc-800/80 p-8 flex flex-col space-y-4 rounded-none hover:border-amber-500/30 transition-all duration-300 ${
                  idx === 2 
                    ? "md:col-span-12 lg:col-span-4" 
                    : "md:col-span-6 lg:col-span-4"
                }`}
              >
                <div className="p-3 bg-zinc-900 inline-block rounded-none border border-zinc-800 w-fit">
                  {val.icon}
                </div>
                <h3 className="text-lg font-bold uppercase tracking-wide text-stone-100">
                  {val.title}
                </h3>
                <p className="text-stone-400 text-xs leading-relaxed font-light">
                  {val.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="relative bg-zinc-950 py-24 px-6 md:px-12 text-white rounded-none border-t border-zinc-800 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.hero}
            alt="Kontakt baggrund"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/80" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left Column: Contact info */}
          <motion.div
            variants={slideInLeft}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-8 flex flex-col justify-between"
          >
            <div className="space-y-6">
              <span className="text-xs font-bold uppercase tracking-widest text-amber-500">
                Kontakt os i dag
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold uppercase tracking-wider text-stone-50 leading-tight">
                Lad os tage en uforpligtende snak om dit projekt
              </h2>
              <p className="text-stone-400 font-light leading-relaxed">
                Uanset om du skal have lagt nyt tag, bygget en smuk træterrasse eller udført andet tømrerarbejde, står vi klar til at rådgive dig. Vi udarbejder altid et skriftligt, gennemskueligt og uforpligtende tilbud.
              </p>
              <div className="space-y-4 pt-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-zinc-950 border border-zinc-800 flex items-center justify-center text-amber-500">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">Ring direkte</span>
                    <a
                      href={`tel:${CONTACT_INFO.phone}`}
                      className="text-sm font-bold text-stone-100 hover:text-amber-500 transition-colors duration-200 whitespace-nowrap"
                    >
                      {CONTACT_INFO.phoneFormatted}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-zinc-950 border border-zinc-800 flex items-center justify-center text-amber-500">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">E-mail</span>
                    <a
                      href={`mailto:${CONTACT_INFO.email}`}
                      className="text-sm font-bold text-stone-100 hover:text-amber-500 transition-colors duration-200 break-all"
                    >
                      {CONTACT_INFO.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-zinc-950 border border-zinc-800 flex items-center justify-center text-amber-500">
                    <Home className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold uppercase tracking-widest text-zinc-500">Adresse</span>
                    <span className="text-sm font-semibold text-stone-200">
                      {CONTACT_INFO.address}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-xs text-stone-500 border-t border-zinc-800/80 pt-6">
              Nordsjællands Tømrer og Snedker · CVR: {CONTACT_INFO.cvr} · Base i Helsingør
            </div>
          </motion.div>

          {/* Right Column: Form */}
          <motion.div
            variants={slideInRight}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true, margin: "-100px" }}
            className="bg-zinc-950 border border-zinc-800/80 p-8 md:p-10 rounded-none"
          >
            <h3 className="text-lg font-bold uppercase tracking-wider text-stone-100 mb-6">
              Send os en besked
            </h3>
            <ContactForm />
          </motion.div>
        </div>
      </section>
    </>
  );
}
